import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db, defaultDb } from '../firebase/config';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';

export interface FeedbackItem {
  id: string;
  userId: string;
  feedback: string;
  imageUrl?: string;
  createdAt: Timestamp;
  adminResponse?: string;
  adminResponseAt?: Timestamp;
}

export interface AppUpdate {
  id: string;
  message: string;
  createdAt: Timestamp;
}

export interface PendingFeedbackItem {
  id: string;
  userId: string;
  feedback: string;
  imageFile?: File;
  imageUrl?: string;
  createdAt: Date;
  retryCount?: number;
}

export interface OldFeedbackItem {
  id: string;
  message: string;
  response?: string;
  imageUrl?: string;
  timestamp: Timestamp;
}

// Hook for feedback list management - reads from existing 'feedback' collection
export function useFeedbackList() {
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Function to fetch feedback data
  const fetchFeedbackData = useCallback(async (useCache = true) => {
    const cacheKey = 'feedback-list';
    let cachedData = null;
    let hasCachedData = false;
    
    // Always try to get cached data first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        cachedData = JSON.parse(cached);
        hasCachedData = true;
      }
    } catch (error) {
      console.warn('[FEEDBACK_CACHE] Failed to parse cached data:', error);
    }
    
    // If offline, use any available cache regardless of age
    if (isOffline && hasCachedData) {
      console.log('[FEEDBACK_CACHE] Using cached data (offline mode)');
      if (!Array.isArray(cachedData?.data)) {
        throw new Error('[FEEDBACK_CACHE] Corrupt cache – no data array');
      }
      setFeedbackList(cachedData.data.map((item: any) => ({
        ...item,
        createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds),
        adminResponseAt: item.adminResponseAt
          ? new Timestamp(item.adminResponseAt.seconds, item.adminResponseAt.nanoseconds)
          : undefined
      })));
      setIsFromCache(true);
      setLastUpdated(new Date(cachedData.timestamp));
      setLoading(false);
      setError(null);
      return;
    }
    
    // If online and have fresh cache, use it first (but still fetch fresh data in background)
    if (useCache && hasCachedData) {
      const age = Date.now() - cachedData.timestamp;
      // Use cache if less than 30 minutes old (increased from 5 minutes)
      if (age < 1800000) { // 30 minutes
        console.log('[FEEDBACK_CACHE] Using cached feedback list');
        setFeedbackList(cachedData.data.map((item: any) => ({
          ...item,
          createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds),
          adminResponseAt: item.adminResponseAt ? 
            new Timestamp(item.adminResponseAt.seconds, item.adminResponseAt.nanoseconds) : 
            undefined
        })));
        setIsFromCache(true);
        setLastUpdated(new Date(cachedData.timestamp));
        setLoading(false);
        
        // Don't return here if this is initial load - we want fresh data in background
        if (!useCache) return; // But return if this is a forced refresh
      }
    }

    // Fetch fresh data from Firestore (only if online)
    if (!isOffline) {
      try {
        const q = query(
          collection(db, 'feedback'),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        console.log('[FEEDBACK_FETCH] Fetched fresh data from Firestore');
        
        const feedbacks: FeedbackItem[] = [];
        snapshot.forEach((doc) => {
          feedbacks.push({ id: doc.id, ...doc.data() } as FeedbackItem);
        });
        
        setFeedbackList(feedbacks);
        setIsFromCache(false);
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);

        // Cache the data
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: feedbacks,
            timestamp: Date.now()
          }));
          console.log('[FEEDBACK_CACHE] Cached feedback list');
        } catch (error) {
          console.warn('[FEEDBACK_CACHE] Failed to cache data:', error);
        }
      } catch (error) {
        console.error('[FEEDBACK_FETCH] Error fetching feedback:', error);
        setError(error as Error);
        setLoading(false);
        
        // If fetch fails and we have cached data, use it as fallback
        if (hasCachedData) {
          console.log('[FEEDBACK_OFFLINE] Using cached data due to network error');
          setFeedbackList(cachedData.data.map((item: any) => ({
            ...item,
            createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds),
            adminResponseAt: item.adminResponseAt ? 
              new Timestamp(item.adminResponseAt.seconds, item.adminResponseAt.nanoseconds) : 
              undefined
          })));
          setIsFromCache(true);
          setLastUpdated(new Date(cachedData.timestamp));
          setError(null); // Clear error since we have fallback data
        }
      }
    } else if (!hasCachedData) {
      // Offline with no cached data
      setLoading(false);
      setError(new Error('No cached data available offline'));
    }
  }, [isOffline]);

  // Load feedback data once on mount
  useEffect(() => {
    fetchFeedbackData(true);
  }, []); // Only run once on mount

  const updateFeedbackList = useCallback(async (updatedList: FeedbackItem[]) => {
    // This is a placeholder - in the new structure we'd update a single document
    // For now, this doesn't do anything since we're reading from collection
    console.warn('[FEEDBACK_UPDATE] Direct list updates not supported with collection-based approach');
  }, []);

  return {
    feedbackList,
    loading,
    error,
    isFromCache,
    isOffline,
    updateFeedbackList,
    lastUpdated,
    fetchFeedbackData
  };
}

// Hook for app updates management - reads from existing 'appUpdates' collection  
export function useAppUpdates() {
  const [updates, setUpdates] = useState<AppUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Function to fetch updates data
  const fetchUpdatesData = useCallback(async (useCache = true) => {
    const cacheKey = 'app-updates';
    let cachedData = null;
    let hasCachedData = false;
    
    // Always try to get cached data first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        cachedData = JSON.parse(cached);
        hasCachedData = true;
      }
    } catch (error) {
      console.warn('[UPDATES_CACHE] Failed to parse cached data:', error);
    }
    
    // If offline, use any available cache regardless of age
    if (isOffline && hasCachedData) {
      // console.log('[UPDATES_CACHE] Using cached data (offline mode)');
      setUpdates(cachedData.data.map((item: any) => ({
        ...item,
        createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds)
      })));
      setIsFromCache(true);
      setLastUpdated(new Date(cachedData.timestamp));
      setLoading(false);
      setError(null);
      return;
    }
    
    // If online and have fresh cache, use it first (but still fetch fresh data in background)
    if (useCache && hasCachedData) {
      const age = Date.now() - cachedData.timestamp;
      // Use cache if less than 30 minutes old (increased from 10 minutes)
      if (age < 1800000) { // 30 minutes
        // console.log('[UPDATES_CACHE] Using cached updates list');
        setUpdates(cachedData.data.map((item: any) => ({
          ...item,
          createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds)
        })));
        setIsFromCache(true);
        setLastUpdated(new Date(cachedData.timestamp));
        setLoading(false);
        
        // Don't return here if this is initial load - we want fresh data in background
        if (!useCache) return; // But return if this is a forced refresh
      }
    }

    // Fetch fresh data from Firestore (only if online)
    if (!isOffline) {
      try {
        const q = query(
          collection(db, 'appUpdates'),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        // console.log('[UPDATES_FETCH] Fetched fresh data from Firestore');
        
        const appUpdates: AppUpdate[] = [];
        snapshot.forEach((doc) => {
          appUpdates.push({ id: doc.id, ...doc.data() } as AppUpdate);
        });
        
        setUpdates(appUpdates);
        setIsFromCache(false);
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);

        // Cache the data
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: appUpdates,
            timestamp: Date.now()
          }));
          // console.log('[UPDATES_CACHE] Cached updates list');
        } catch (error) {
          console.warn('[UPDATES_CACHE] Failed to cache data:', error);
        }
      } catch (error) {
        console.error('[UPDATES_FETCH] Error fetching updates:', error);
        setError(error as Error);
        setLoading(false);
        
        // If fetch fails and we have cached data, use it as fallback
        if (hasCachedData) {
          console.log('[UPDATES_OFFLINE] Using cached data due to network error');
          setUpdates(cachedData.data.map((item: any) => ({
            ...item,
            createdAt: new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds)
          })));
          setIsFromCache(true);
          setLastUpdated(new Date(cachedData.timestamp));
          setError(null); // Clear error since we have fallback data
        }
      }
    } else if (!hasCachedData) {
      // Offline with no cached data
      setLoading(false);
      setError(new Error('No cached data available offline'));
    }
  }, [isOffline]);

  // Load updates data once on mount
  useEffect(() => {
    fetchUpdatesData(true);
  }, []); // Only run once on mount

  const latestUpdate = updates.length > 0 ? updates[0] : null;

  const addUpdate = useCallback(async (message: string) => {
    try {
      await addDoc(collection(db, 'appUpdates'), {
        message: message.trim(),
        createdAt: Timestamp.now()
      });
      console.log('[UPDATES_ADD] Successfully added update');
      
      // Fetch fresh data to see the new update
      await fetchUpdatesData(false);
    } catch (error) {
      console.error('[UPDATES_ADD] Error adding update:', error);
      throw error;
    }
  }, [fetchUpdatesData]);

  return {
    updates,
    latestUpdate,
    addUpdate,
    loading,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    fetchUpdatesData
  };
}

// Hook for submitting feedback - writes to existing 'feedback' collection
export function useFeedbackSubmission(onSuccess?: () => Promise<void>) {
  const { currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const submitFeedback = useCallback(async (
    feedback: string,
    imageFile?: File,
    addToPendingQueue?: (feedback: string, imageFile?: File) => string | undefined
  ) => {
    if (!currentUser || !feedback.trim()) {
      throw new Error('Missing required data');
    }

    setSubmitting(true);
    setError(null);

    // If offline, add to pending queue instead of trying to submit
    if (isOffline) {
      try {
        if (addToPendingQueue) {
          addToPendingQueue(feedback, imageFile);
          console.log('[FEEDBACK_SUBMIT] Added to pending queue (offline)');
          setSubmitting(false);
          return { queued: true };
        } else {
          throw new Error('Offline queue not available');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setSubmitting(false);
        throw err;
      }
    }

    try {
      let imageUrl = '';

      // Upload image if provided, with validation
      if (imageFile) {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(imageFile.type)) {
          throw new Error('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
        }

        // Validate file size (max 5MB)
        const maxSizeBytes = 5 * 1024 * 1024;
        if (imageFile.size > maxSizeBytes) {
          throw new Error('File too large. Maximum size is 5MB.');
        }

        // Upload to Firebase Storage
        const imageRef = ref(
          storage,
          `feedback-images/${Date.now()}_${encodeURIComponent(imageFile.name)}`
        );
        const snapshot = await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      // Add feedback to existing collection structure
      await addDoc(collection(db, 'feedback'), {
        userId: currentUser.uid,
        feedback: feedback.trim(),
        imageUrl,
        createdAt: Timestamp.now()
      });

      console.log('[FEEDBACK_SUBMIT] Successfully submitted feedback');
      
      // Fetch fresh data to see the new feedback
      if (onSuccess) {
        await onSuccess();
      }
      
      setSubmitting(false);
      return { submitted: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setSubmitting(false);
      
      // If network error and we have pending queue, add to queue
      if (!navigator.onLine && addToPendingQueue) {
        try {
          addToPendingQueue(feedback, imageFile);
          console.log('[FEEDBACK_SUBMIT] Network failed, added to pending queue');
          setError('Added to queue - will send when online');
          return { queued: true };
        } catch (queueErr) {
          console.error('[FEEDBACK_SUBMIT] Failed to add to queue:', queueErr);
        }
      }
      
      throw err;
    }
  }, [currentUser, onSuccess, isOffline]);

  return {
    submitFeedback,
    submitting,
    error,
    isOffline
  };
}

// Hook for admin feedback management - works with existing 'feedback' collection
export function useFeedbackManagement() {
  const { feedbackList, loading, error, isFromCache, isOffline, fetchFeedbackData } = useFeedbackList();

  const respondToFeedback = useCallback(async (
    feedbackId: string,
    response: string
  ) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        adminResponse: response.trim(),
        adminResponseAt: Timestamp.now()
      });
      console.log('[FEEDBACK_RESPOND] Successfully added response');
      
      // Fetch fresh data to see the response
      await fetchFeedbackData(false);
    } catch (error) {
      console.error('[FEEDBACK_RESPOND] Error responding to feedback:', error);
      throw error;
    }
  }, [fetchFeedbackData]);

  const deleteFeedback = useCallback(async (feedbackId: string) => {
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
      console.log('[FEEDBACK_DELETE] Successfully deleted feedback');
      
      // Fetch fresh data to see the deletion
      await fetchFeedbackData(false);
    } catch (error) {
      console.error('[FEEDBACK_DELETE] Error deleting feedback:', error);
      throw error;
    }
  }, [fetchFeedbackData]);

  const unrespondedCount = feedbackList.filter(item => !item.adminResponse).length;

  return {
    feedbackList,
    respondToFeedback,
    deleteFeedback,
    unrespondedCount,
    loading,
    error,
    isFromCache,
    isOffline
  };
}

// Helper hook for formatting and utilities
export function useFeedbackHelpers() {
  const formatDate = useCallback((timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  return {
    formatDate
  };
}

// Hook for managing pending feedback submissions (offline queue)
export function usePendingFeedback(onSyncComplete?: () => Promise<void>) {
  const { currentUser } = useAuth();
  const [pendingItems, setPendingItems] = useState<PendingFeedbackItem[]>([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const PENDING_FEEDBACK_KEY = 'pending-feedback-submissions';

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Don't call syncPendingSubmissions here - let the auto-sync effect handle it
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending items from localStorage on mount
  useEffect(() => {
    loadPendingItems();
  }, []);

  const loadPendingItems = useCallback(() => {
    try {
      const stored = localStorage.getItem(PENDING_FEEDBACK_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert stored dates back to Date objects
        const items = parsed.map((item: any) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          // Validate required fields
          id: item.id || `recovered_${Date.now()}`,
          userId: item.userId || '',
          feedback: item.feedback || '',
          retryCount: typeof item.retryCount === 'number' ? item.retryCount : 0
        }));
        setPendingItems(items);
        console.log('[PENDING_FEEDBACK] Loaded pending items:', items.length);
      }
    } catch (error) {
      console.error('[PENDING_FEEDBACK] Failed to load pending items:', error);
    }
  }, []);

  const savePendingItems = useCallback((items: PendingFeedbackItem[]) => {
    try {
      // Remove imageFile before storing (File objects can't be serialized)
      const itemsToStore = items.map(({ imageFile, ...item }) => item);
    localStorage.setItem(PENDING_FEEDBACK_KEY, JSON.stringify(itemsToStore));
    console.log('[PENDING_FEEDBACK] Saved pending items:', items.length);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error(
        '[PENDING_FEEDBACK] localStorage quota exceeded, clearing old items'
      );
      // Could implement LRU eviction or user notification here
    } else {
      console.error(
        '[PENDING_FEEDBACK] Failed to save pending items:', 
        error
      );
    }
  }
}, []);

  const addPendingItem = useCallback((feedback: string, imageFile?: File) => {
    if (!currentUser) return;

    const newItem: PendingFeedbackItem = {
      id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: currentUser.uid,
      feedback: feedback.trim(),
      imageFile,
      createdAt: new Date(),
      retryCount: 0,
    };

    const updatedItems = [...pendingItems, newItem];
    setPendingItems(updatedItems);
    savePendingItems(updatedItems);
    
    console.log('[PENDING_FEEDBACK] Added pending item:', newItem.id);
    return newItem.id;
  }, [currentUser, pendingItems, savePendingItems]);

  const removePendingItem = useCallback((itemId: string) => {
    const updatedItems = pendingItems.filter(item => item.id !== itemId);
    setPendingItems(updatedItems);
    savePendingItems(updatedItems);
    console.log('[PENDING_FEEDBACK] Removed pending item:', itemId);
  }, [pendingItems, savePendingItems]);

  const syncPendingSubmissions = useCallback(async () => {
    if (isOffline || pendingItems.length === 0 || syncingRef.current) return;

    syncingRef.current = true;
    setSyncing(true);
    console.log('[PENDING_FEEDBACK] Starting sync of', pendingItems.length, 'items');

    const itemsToRetry = [...pendingItems];
    const successfullyProcessed: string[] = [];
    
    for (const item of itemsToRetry) {
      try {
        let imageUrl = '';
        
        // Upload image if it exists (won't exist after page reload due to File object limitation)
        if (item.imageFile) {
          const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
          const maxSize = 5 * 1024 * 1024; // 5MB

          if (!allowedTypes.includes(item.imageFile.type)) {
            throw new Error('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
          }
          if (item.imageFile.size > maxSize) {
            throw new Error('File too large. Maximum size is 5MB.');
          }
          
          const imageRef = ref(storage, `feedback-images/${Date.now()}_${item.imageFile.name}`);
          const snapshot = await uploadBytes(imageRef, item.imageFile);
          imageUrl = await getDownloadURL(snapshot.ref);
        } else if (item.imageUrl) {
          // Use previously uploaded image URL if available
          imageUrl = item.imageUrl;
        }

        // Submit to Firestore
        await addDoc(collection(db, 'feedback'), {
          userId: item.userId,
          feedback: item.feedback,
          imageUrl,
          createdAt: Timestamp.fromDate(item.createdAt)
        });

        console.log('[PENDING_FEEDBACK] Successfully synced item:', item.id);
        successfullyProcessed.push(item.id);
        
      } catch (error) {
        console.error('[PENDING_FEEDBACK] Failed to sync item:', item.id, error);
        
        // Increment retry count
        const updatedItems = pendingItems.map(pendingItem => 
          pendingItem.id === item.id 
            ? { ...pendingItem, retryCount: (pendingItem.retryCount || 0) + 1 }
            : pendingItem
        );
        setPendingItems(updatedItems);
        savePendingItems(updatedItems);
      }
    }

    // Remove successfully processed items in batch
    if (successfullyProcessed.length > 0) {
      const remainingItems = pendingItems.filter(item => !successfullyProcessed.includes(item.id));
      setPendingItems(remainingItems);
      savePendingItems(remainingItems);
      
      // Call the callback to refetch feedback data after successful sync
      if (onSyncComplete) {
        try {
          await onSyncComplete();
          console.log('[PENDING_FEEDBACK] Successfully refetched feedback data after sync');
        } catch (error) {
          console.error('[PENDING_FEEDBACK] Failed to refetch feedback data after sync:', error);
        }
      }
    }

    syncingRef.current = false;
    setSyncing(false);
  }, [isOffline, pendingItems, removePendingItem, savePendingItems, onSyncComplete]);

  // Auto-sync when conditions are right
  useEffect(() => {
    if (!isOffline && pendingItems.length > 0 && !syncingRef.current) {
      const timer = setTimeout(() => {
        syncPendingSubmissions();
      }, 1000); // Small delay to avoid rapid syncing

      return () => clearTimeout(timer);
    }
  }, [isOffline, pendingItems.length, syncPendingSubmissions]);

  return {
    pendingItems,
    addPendingItem,
    removePendingItem,
    syncPendingSubmissions,
    syncing,
    isOffline
  };
}

// Hook for old feedback from default database
export function useOldFeedback() {
  const [oldFeedbackList, setOldFeedbackList] = useState<OldFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Function to fetch old feedback data
  const fetchOldFeedbackData = useCallback(async (useCache = true) => {
    const cacheKey = 'old-feedback-list';
    let cachedData = null;
    let hasCachedData = false;
    
    // Always try to get cached data first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        cachedData = JSON.parse(cached);
        hasCachedData = true;
      }
    } catch (error) {
      console.warn('[OLD_FEEDBACK_CACHE] Failed to parse cached data:', error);
    }
    
    // If offline, use any available cache regardless of age
    if (isOffline && hasCachedData) {
      console.log('[OLD_FEEDBACK_CACHE] Using cached data (offline mode)');
      if (!Array.isArray(cachedData?.data)) {
        throw new Error('[OLD_FEEDBACK_CACHE] Corrupt cache – no data array');
      }
      setOldFeedbackList(cachedData.data.map((item: any) => ({
        ...item,
        timestamp: new Timestamp(item.timestamp.seconds, item.timestamp.nanoseconds)
      })));
      setIsFromCache(true);
      setLastUpdated(new Date(cachedData.timestamp));
      setLoading(false);
      setError(null);
      return;
    }
    
    // If online and have fresh cache, use it first
    if (useCache && hasCachedData) {
      const age = Date.now() - cachedData.timestamp;
      // Use cache if less than 1 hour old
      if (age < 3600000) { // 1 hour
        console.log('[OLD_FEEDBACK_CACHE] Using cached old feedback list');
        setOldFeedbackList(cachedData.data.map((item: any) => ({
          ...item,
          timestamp: new Timestamp(item.timestamp.seconds, item.timestamp.nanoseconds)
        })));
        setIsFromCache(true);
        setLastUpdated(new Date(cachedData.timestamp));
        setLoading(false);
        
        // Don't return here if this is initial load - we want fresh data in background
        if (!useCache) return; // But return if this is a forced refresh
      }
    }

    // Fetch fresh data from default Firestore (only if online)
    if (!isOffline) {
      try {
        const q = query(
          collection(defaultDb, 'feedback'),
          orderBy('timestamp', 'desc')
        );

        const snapshot = await getDocs(q);
        console.log('[OLD_FEEDBACK_FETCH] Fetched fresh data from default Firestore');
        
        const oldFeedbacks: OldFeedbackItem[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          oldFeedbacks.push({ 
            id: doc.id, 
            message: data.message || '',
            response: data.response || '',
            imageUrl: data.imageUrl || '',
            timestamp: data.timestamp
          } as OldFeedbackItem);
        });
        
        setOldFeedbackList(oldFeedbacks);
        setIsFromCache(false);
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);

        // Cache the data
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: oldFeedbacks,
            timestamp: Date.now()
          }));
          console.log('[OLD_FEEDBACK_CACHE] Cached old feedback list');
        } catch (error) {
          console.warn('[OLD_FEEDBACK_CACHE] Failed to cache data:', error);
        }
      } catch (error) {
        console.error('[OLD_FEEDBACK_FETCH] Error fetching old feedback:', error);
        setError(error as Error);
        setLoading(false);
        
        // If fetch fails and we have cached data, use it as fallback
        if (hasCachedData) {
          console.log('[OLD_FEEDBACK_OFFLINE] Using cached data due to network error');
          setOldFeedbackList(cachedData.data.map((item: any) => ({
            ...item,
            timestamp: new Timestamp(item.timestamp.seconds, item.timestamp.nanoseconds)
          })));
          setIsFromCache(true);
          setLastUpdated(new Date(cachedData.timestamp));
          setError(null); // Clear error since we have fallback data
        }
      }
    } else if (!hasCachedData) {
      // Offline with no cached data
      setLoading(false);
      setError(new Error('No cached data available offline'));
    }
  }, [isOffline]);

  // Load old feedback data once on mount
  useEffect(() => {
    fetchOldFeedbackData(true);
  }, []); // Only run once on mount

  return {
    oldFeedbackList,
    loading,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    fetchOldFeedbackData
  };
} 